import React, { useCallback, useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import {
  FaChevronLeft,
  FaChevronRight,
  FaClipboard,
  FaComments,
  FaFlag,
  FaHeart,
  FaRegHeart,
  FaShare,
  FaShieldAlt,
} from "react-icons/fa";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./CardModal.css";
import CardMedia from "./CardMedia";
import ShareCardEditor from "./ShareCardEditor";
import CardOwnerControls from "./CardOwnerControls";
import CardPicker from "../CardPicker";
import CardRating from "../CardRating";
import CommentsSection from "./CommentsSection";
import RelativeDate from "./RelativeDate";
import ReportModal from "../ReportModal";
import VotingSection from "../VotingSection";
import { useUser } from "../../UserContext";
import { useModeratorAction } from "../../ModeratorActionContext";
import { buildUrlWithParams } from "../utils.js";

const clamp = (value, min = -1, max = 1) =>
  Math.min(max, Math.max(min, value));

const PortalPreview = ({ cardInfo, onClose }) => {
  const portalRef = useRef(null);
  const pointerTargetRef = useRef({ x: 0, y: 0 });
  const tiltTargetRef = useRef({ x: 0, y: 0 });
  const [motionState, setMotionState] = useState("hidden");

  useEffect(() => {
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !("DeviceOrientationEvent" in window)
    ) {
      return;
    }

    if (
      typeof window.DeviceOrientationEvent.requestPermission === "function"
    ) {
      // The effect is decorative, so never ask the user for sensor permission.
      return;
    }

    // Use tilt only on implementations that expose it without a prompt.
    setMotionState("enabled");
  }, []);

  useEffect(() => {
    if (motionState !== "enabled") return undefined;

    const origin = { beta: null, gamma: null };
    const handleOrientation = ({ beta, gamma }) => {
      if (beta == null || gamma == null) return;

      if (origin.beta == null) {
        origin.beta = beta;
        origin.gamma = gamma;
      }

      tiltTargetRef.current = {
        x: clamp((gamma - origin.gamma) / 20),
        y: clamp((beta - origin.beta) / 20),
      };
    };

    window.addEventListener("deviceorientation", handleOrientation, {
      passive: true,
    });

    return () => {
      window.removeEventListener("deviceorientation", handleOrientation);
      tiltTargetRef.current = { x: 0, y: 0 };
    };
  }, [motionState]);

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    if (reducedMotion.matches) return undefined;

    let frameId;
    const current = { x: 0, y: 0 };

    const handlePointerMove = (event) => {
      if (event.pointerType === "touch" || !portalRef.current) return;

      const rect = portalRef.current.getBoundingClientRect();
      const x =
        (event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
      const y =
        (event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2);
      const distance = Math.hypot(x, y);
      const falloff = clamp((2.1 - distance) / 1.1, 0, 1);

      pointerTargetRef.current = {
        x: clamp(x) * falloff,
        y: clamp(y) * falloff,
      };
    };

    const clearPointer = (event) => {
      if (!event.relatedTarget) {
        pointerTargetRef.current = { x: 0, y: 0 };
      }
    };

    const animate = () => {
      const portal = portalRef.current;
      const pointer = pointerTargetRef.current;
      const tilt = tiltTargetRef.current;
      const desiredX = clamp(pointer.x + tilt.x * 0.75);
      const desiredY = clamp(pointer.y + tilt.y * 0.75);

      current.x += (desiredX - current.x) * 0.085;
      current.y += (desiredY - current.y) * 0.085;

      if (portal) {
        portal.style.transform = `perspective(900px) rotateX(${
          current.y * -7
        }deg) rotateY(${current.x * 7}deg)`;

        portal.querySelectorAll("[data-portal-layer]").forEach((layer) => {
          const depth = Number(layer.dataset.depth);
          const z = Number(layer.dataset.z);
          layer.style.transform = `translate3d(${current.x * depth}px, ${
            current.y * depth
          }px, ${z}px)`;
        });
      }

      frameId = window.requestAnimationFrame(animate);
    };

    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("pointerout", clearPointer);
    window.addEventListener("blur", clearPointer);
    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerout", clearPointer);
      window.removeEventListener("blur", clearPointer);
    };
  }, []);

  return (
    <div
      className="col-md d-flex flex-column align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflowX: "hidden",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes portal-spin-left {
          from { transform: scaleX(-1) rotate(0deg); }
          to { transform: scaleX(-1) rotate(-360deg); }
        }

        @keyframes portal-spin-right {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .portal-spin { animation: none !important; }
        }
      `}</style>

      <a
        href={`/sets/${cardInfo.setOpenID}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-decoration-none text-center"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          ref={portalRef}
          className="position-relative mx-auto mb-3"
          style={{
            width: "350px",
            height: "350px",
            transformStyle: "preserve-3d",
            willChange: "transform",
          }}
        >
          <div
            data-portal-layer
            data-depth="-5"
            data-z="0"
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{ willChange: "transform" }}
          >
            <div
              className="w-100 h-100 rounded-circle overflow-hidden"
              style={{
                maskImage:
                  "radial-gradient(circle, black 25%, transparent 70%)",
                WebkitMaskImage:
                  "radial-gradient(circle, black 25%, transparent 70%)",
              }}
            >
              <img
                src={`/cards/${cardInfo.cardId2}.${cardInfo.cardFormat}`}
                alt="Set Link"
                className="w-100 h-100"
                style={{ objectFit: "cover", transform: "scale(1.03)" }}
              />
            </div>
          </div>

          {[
            {
              depth: 7,
              z: 18,
              size: "120%",
              top: "-12%",
              left: "-9%",
              opacity: 0.3,
              shadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              animation: "portal-spin-left 55s linear infinite",
            },
            {
              depth: 14,
              z: 36,
              size: "150%",
              top: "-26%",
              left: "-24%",
              opacity: 0.6,
              shadow: "0 8px 12px rgba(0, 0, 0, 0.2)",
              animation: "portal-spin-right 35s linear infinite",
            },
            {
              depth: 22,
              z: 58,
              size: "150%",
              top: "-26%",
              left: "-24%",
              opacity: 0.5,
              shadow: "0 12px 18px rgba(0, 0, 0, 0.24)",
              animation: "portal-spin-left 15s linear infinite",
            },
          ].map((layer) => (
            <div
              key={layer.z}
              data-portal-layer
              data-depth={layer.depth}
              data-z={layer.z}
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{ pointerEvents: "none", willChange: "transform" }}
            >
              <img
                src="/images/swirly.webp"
                alt=""
                className="portal-spin position-absolute"
                style={{
                  width: layer.size,
                  height: layer.size,
                  top: layer.top,
                  left: layer.left,
                  objectFit: "cover",
                  opacity: layer.opacity,
                  filter: `drop-shadow(${layer.shadow})`,
                  animation: layer.animation,
                }}
              />
            </div>
          ))}
        </div>

        <div className="fw-bold text-white fs-4" style={{ marginTop: "80px" }}>
          Click to open the set in a new tab.
        </div>
      </a>

    </div>
  );
};

const CardModal = ({ card, user, onClose }) => {
  const [cardDetails, setCardDetails] = useState(null);
  const [isInGallery, setIsInGallery] = useState(false);
  const [putError, setPutError] = useState(null);
  const [totalComments, setTotalComments] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState("");
  const [reportItemId, setReportItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShareEditor, setShowShareEditor] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [cardPickerType, setCardPickerType] = useState("edit");

  const { openModModal } = useModeratorAction();
  const { settings, cards = [], refreshOrganizerData } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const isContestPage = location.pathname.includes("/contests/");

  const currentCardIndex = cards.findIndex(
    (availableCard) => Number(availableCard.id) === Number(card),
  );
  const prevCardId =
    currentCardIndex > 0 ? cards[currentCardIndex - 1].id : null;
  const nextCardId =
    currentCardIndex >= 0 && currentCardIndex < cards.length - 1
      ? cards[currentCardIndex + 1].id
      : null;

  const fetchCardDetails = useCallback(async () => {
    if (!card) return;

    const response = await fetch(
      `/api/card/card-stuff_react.php?cardid=${card}`,
    );
    const data = await response.json();
    setCardDetails(data);
  }, [card]);

  useEffect(() => {
    setCardDetails(null);
    fetchCardDetails();
  }, [fetchCardDetails]);

  useEffect(() => {
    if (!cardDetails) return;

    const approvedGallery = Number(cardDetails.cardInfo.approvedGallery);
    setIsInGallery(approvedGallery === 1 || approvedGallery === 2);
  }, [cardDetails]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "INPUT"
      )
        return;
      if (!isContestPage) return;

      if (event.key === "ArrowLeft" && prevCardId) {
        navigate(buildUrlWithParams({ selectedCard: prevCardId }));
      } else if (event.key === "ArrowRight" && nextCardId) {
        navigate(buildUrlWithParams({ selectedCard: nextCardId }));
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isContestPage, navigate, nextCardId, prevCardId]);

  const openReportModal = (type, id) => {
    setReportType(type);
    setReportItemId(id);
    setShowReportModal(true);
  };

  const handleClosePicker = () => {
    setShowPicker(false);
    if (cardPickerType === "add" || cardPickerType === "delete") {
      fetchCardDetails();
      refreshOrganizerData();
    }
  };

  if (!cardDetails) {
    return (
      <div
        className="card-modal d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
        onClick={onClose}
      >
        Loading...
      </div>
    );
  }

  const { cardInfo, tokens = [] } = cardDetails;
  const cardsPerRow = settings.cardsPerRow;
  const hasTokens = tokens.length > 0;
  const isCardOwner =
    user && Number(user.userid) === Number(cardInfo.creatorId);

  const handleGalleryToggle = async () => {
    try {
      const response = await axios.post("/api/card/putingallery.php", {
        action: isInGallery ? "remove" : "add",
        cardId: cardInfo.cardId,
      });

      if (!response.data.success) {
        setPutError(response.data.message || "Failed to update gallery status");
        return;
      }

      const nextIsInGallery = !isInGallery;
      setIsInGallery(nextIsInGallery);
      setCardDetails((previousDetails) => ({
        ...previousDetails,
        cardInfo: {
          ...previousDetails.cardInfo,
          approvedGallery: nextIsInGallery ? 2 : 0,
        },
      }));
      setPutError(null);
    } catch (error) {
      console.error("Error updating gallery status:", error);
      alert("An error occurred while updating the gallery status");
    }
  };

  const handleCardEditButton = () => {
    if (hasTokens) {
      setCardPickerType("edit");
      setShowPicker(true);
      return;
    }

    window.location.href = `/?action=edit&id=${cardInfo.cardId2}&nid=${cardInfo.cardId}&redirect=1`;
  };

  const handleDelete = async () => {
    try {
      const response = await axios.post("/api/card/delete-card.php", {
        action: "delete",
        cid: cardInfo.cardId2,
      });

      if (!response.data.success) {
        alert("Failed to delete card");
        return;
      }

      setShowDeleteModal(false);
      refreshOrganizerData();
      onClose();
    } catch (error) {
      console.error(error);
      alert("An error occurred while deleting the card");
    }
  };

  const handleFavorite = async () => {
    try {
      const response = await axios.post("/api/card/favorite.php", {
        cid: cardInfo.cardId,
      });

      setCardDetails((previousDetails) => ({
        ...previousDetails,
        cardInfo: {
          ...previousDetails.cardInfo,
          favstate: Number(response.data.state) === 1 ? 1 : 0,
        },
      }));
    } catch {
      alert("An error occurred while favoriting the card");
    }
  };

  const handleDownloadImage = async () => {
    try {
      const response = await fetch(`/api/card/share-image.php?card_id=${card}`);
      if (!response.ok) throw new Error("Network response was not ok");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `card_${card}.webp`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  const handleCopyLink = (event) => {
    const button = event.currentTarget;
    button.classList.remove("btn-primary");
    button.classList.add("btn-success");
    window.setTimeout(() => {
      button.classList.remove("btn-success");
      button.classList.add("btn-primary");
    }, 1000);

    navigator.clipboard.writeText(
      `https://www.hearthcards.net/gallery/?selectedCard=${card}`,
    );
  };

  return (
    <>
      <div className="card-modal">
        {isContestPage && prevCardId && (
          <Link
            to={buildUrlWithParams({ selectedCard: prevCardId })}
            preventScrollReset
            className="position-fixed top-50 translate-middle-y modal-arrows"
            style={{
              transform: "translateY(-50%)",
              zIndex: 1050,
              left: "10px",
              cursor: "pointer",
              textDecoration: "none",
            }}
            aria-label="Previous Card"
          >
            <FaChevronLeft
              size={40}
              className="text-white bg-dark bg-opacity-50 rounded-circle p-2"
            />
          </Link>
        )}

        {isContestPage && nextCardId && (
          <Link
            to={buildUrlWithParams({ selectedCard: nextCardId })}
            preventScrollReset
            className="position-fixed top-50 translate-middle-y modal-arrows"
            style={{
              transform: "translateY(-50%)",
              zIndex: 1050,
              right: "580px",
              cursor: "pointer",
              textDecoration: "none",
            }}
            aria-label="Next Card"
          >
            <FaChevronRight
              size={40}
              className="text-white bg-dark bg-opacity-50 rounded-circle p-2"
            />
          </Link>
        )}

        {showPicker && (
          <CardPicker
            type={cardPickerType}
            card={cardInfo.cardId2}
            onConfirm={() => {}}
            onClose={handleClosePicker}
          />
        )}

        <div className="card-modal-body">
          <button
            className="btn-close position-absolute end-0 top-0 m-3"
            onClick={onClose}
          />

          <div
            className="d-md-none position-absolute end-0 top-0 mt-5 me-3 text-center"
            onClick={() => {
              document
                .querySelector(".commentsHeader")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            title="Go to comments"
          >
            <button className="goToCommentsButton">
              <FaComments size={20} />
            </button>
            <div className="small mt-0">{totalComments}</div>
          </div>

<div className="row">
  {cardInfo.setOpenID && Number(cardInfo.setOpenID) > 0 ? (
    <PortalPreview cardInfo={cardInfo} onClose={onClose} />
  ) : (
    <CardMedia
      cardInfo={cardInfo}
      tokens={tokens}
      cardsPerRow={cardsPerRow}
      onImageClick={onClose}
    />
  )}

  <div className="col-md card-modal-right text-center">
              <h3>{cardInfo.cardName}</h3>
              <p>
                <RelativeDate value={cardInfo.creationDate} />
                {cardInfo.creatorName && (
                  <>
                    {" "}
                    by{" "}
                    <strong>
                      <Link
                        to={`/gallery/user/${cardInfo.creatorId}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        {cardInfo.creatorName}
                      </Link>
                    </strong>
                  </>
                )}
              </p>

              <Button
                variant="danger"
                size="sm"
                className="me-2"
                onClick={() => openReportModal("card", cardInfo.cardId)}
              >
                <FaFlag /> Report
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="me-2"
                onClick={() => setShowShareModal(true)}
              >
                <FaShare /> Share
              </Button>
              {user && (
                <Button variant="primary" size="sm" onClick={handleFavorite}>
                  {Number(cardInfo.favstate) === 1 ? (
                    <FaHeart color="red" />
                  ) : (
                    <FaRegHeart />
                  )}{" "}
                  {Number(cardInfo.favstate) === 1 ? "Favorited" : "Favorite"}
                </Button>
              )}
              {user?.mod === true && !isCardOwner && (
                <Button
                  variant="danger"
                  size="sm"
                  className="ms-2"
                  onClick={() => openModModal("card", cardInfo.cardId)}
                >
                  <FaShieldAlt /> Remove Card
                </Button>
              )}

              {Math.round(
                (new Date() - new Date(cardInfo.creationDate)) /
                  (1000 * 60 * 60 * 24),
              ) <= 180 &&
                (Number(cardInfo.approvedGallery) === 1 ||
                  Number(cardInfo.approvedGallery) === 2) && (
                  <CardRating
                    cardid={cardInfo.cardId}
                    initialRating={cardInfo.cardRating}
                    totalVotes={cardInfo.cardVoted}
                    userRated={cardInfo.uRating || 0}
                  />
                )}

              {Number(cardInfo.approvedGallery) === 5 &&
                cardInfo.contest_data && <VotingSection cardInfo={cardInfo} />}

              {cardInfo.removeReason && (
                <div className="p-3 mb-2 bg-danger text-white mt-2">
                  {cardInfo.removeReason}
                </div>
              )}
              {putError && (
                <div className="p-3 mb-2 alert alert-danger mt-2" role="alert">
                  {putError}
                </div>
              )}

              {!cardInfo.setOpenID && Number(cardInfo.setOpenID) === 0 &&
              isCardOwner && Number(cardInfo.approvedGallery) !== 6 && (
                <CardOwnerControls
                  approvedGallery={cardInfo.approvedGallery}
                  hasTokens={hasTokens}
                  isInGallery={isInGallery}
                  onDelete={() => setShowDeleteModal(true)}
                  onEdit={handleCardEditButton}
                  onImport={() => {
                    window.location.href = `https://www.hearthcards.net/#${cardInfo.cardId2}`;
                  }}
                  onGalleryToggle={handleGalleryToggle}
                  onAddTokens={() => {
                    setCardPickerType("add");
                    setShowPicker(true);
                  }}
                  onRemoveTokens={() => {
                    setCardPickerType("delete");
                    setShowPicker(true);
                  }}
                />
              )}
              <CommentsSection
                cardInfo={cardInfo}
                user={user}
                onReport={openReportModal}
                onModeratorRemove={(commentId) =>
                  openModModal("comment", commentId)
                }
                onTotalCommentsChange={setTotalComments}
              />
            </div>
          </div>
        </div>
      </div>

      <ReportModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        reportType={reportType}
        reportItemId={reportItemId}
      />

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this card?</p>
          <div className="d-flex justify-content-end">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" className="ms-2" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Share Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Share this card with others using the link below:</p>
          <InputGroup>
            <Form.Control
              type="text"
              value={`https://www.hearthcards.net/gallery/?selectedCard=${card}`}
              readOnly
            />
            <Button
              variant="primary"
              className="d-flex align-items-center justify-content-center"
              onClick={handleCopyLink}
            >
              <FaClipboard />
            </Button>
{user &&
  Number(user.rank) === 1 &&
  tokens.length <= 4 && (
    <Button
      className="w-100 mt-3"
      variant="primary"
      onClick={() => {
        setShowShareModal(false);
        setShowShareEditor(true);
      }}
    >
      Create Share Image
    </Button>
  )}
            {isCardOwner && (
              <Button
                className="w-100 mt-2"
                variant="outline-primary"
                onClick={handleDownloadImage}
              >
                Download original image
              </Button>
            )}
          </InputGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ShareCardEditor
        show={showShareEditor}
        onHide={() => setShowShareEditor(false)}
        cardInfo={cardInfo}
        tokens={tokens}
      />
    </>
  );
};

export default CardModal;
